import { useEffect, useMemo, useState } from 'react';
import site from './site.config.js';
import {
  contentMap,
  createCheckout,
  getCatalog,
  getPaymentConfiguration,
  getPublishedContent,
  getQuote,
} from './api.js';

const CART_KEY = `${site.slug}:cart:v1`;

const audienceEdits = [
  {
    id: 'creator',
    label: 'Beauty creator',
    title: 'Three signature looks built for content.',
    body: 'Start with a natural hook, a soft-glam hero, and one memorable statement style. Test saves, comments, waitlist intent, and repeat wear before scaling.',
    points: ['Audience-led style edit', 'Camera-ready sample kit', 'Small-run launch path'],
  },
  {
    id: 'pro',
    label: 'Lash tech',
    title: 'A retail extension for your service chair.',
    body: 'Offer take-home strip lashes that complement your clients’ appointments without asking you to build a consumer brand from zero.',
    points: ['Shelf-ready assortment', 'Reorder-friendly SKUs', 'Professional sampling'],
  },
  {
    id: 'founder',
    label: 'Beauty founder',
    title: 'A focused first collection—not a catalog dump.',
    body: 'Define the customer, wear occasion, price architecture, packaging level, and proof required before production decisions are locked.',
    points: ['Product brief workshop', 'Packaging direction', 'Launch and replenishment plan'],
  },
];

const processSteps = [
  ['01', 'Define the audience', 'Eye shape, makeup mood, wear occasion, channel, target price, and content angle.'],
  ['02', 'Edit & sample', 'Compare fibers, curl, band comfort, length, density, and packaging direction.'],
  ['03', 'Test demand', 'Use samples and creator content to validate interest before a larger production commitment.'],
  ['04', 'Produce & replenish', 'Approve written specifications, labeling, commercial terms, quality checkpoints, and reorder rhythm.'],
];

const programCards = [
  ['Sample edit', 'For early product discovery', 'A focused style comparison with a written feedback sheet.'],
  ['Launch capsule', 'For a first paid release', 'Three audience-matched styles with packaging and a repeatable SKU plan.'],
  ['Wholesale line', 'For pros and boutiques', 'A shelf-ready mix, reorder structure, and account-specific commercial terms.'],
];

function money(value) {
  return new Intl.NumberFormat(site.locale, { style: 'currency', currency: site.currency }).format(Number(value || 0));
}

function published(blocks, key, field, fallback) {
  const value = blocks[key]?.[field];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function mediaFor(product) {
  const all = Array.isArray(product.media) ? product.media : [];
  const video = all.find((item) => item.media_type === 'video');
  const images = all.filter((item) => item.media_type === 'image').slice(0, 10);
  if (!images.length && product.primaryImage) {
    images.push({ id: 'primary', media_type: 'image', url: product.primaryImage, alt_text: product.name });
  }
  return [...(video ? [video] : []), ...images];
}

function Wordmark() {
  return <span className="wordmark"><i aria-hidden="true" />Novodom</span>;
}

function LashIllustration({ kind = 'soft' }) {
  return (
    <div className={`lash-illustration lash-${kind}`} aria-hidden="true">
      <span className="lash-line" />
      {Array.from({ length: 13 }, (_, index) => <i key={index} style={{ '--lash-index': index }} />)}
    </div>
  );
}

function ProductVisual({ product, compact = false }) {
  const media = mediaFor(product);
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [product.id]);
  const selected = media[active];

  return (
    <div className={`product-visual ${compact ? 'is-compact' : ''}`}>
      <div className="product-stage">
        {!selected && <><LashIllustration kind={product.visual} /><span className="preview-stamp">Preview assortment</span></>}
        {selected?.media_type === 'video' ? (
          <video controls playsInline poster={selected.thumbnail_url || undefined}><source src={selected.url} /></video>
        ) : selected ? <img src={selected.url} alt={selected.alt_text || product.name} /> : null}
      </div>
      {!compact && media.length > 1 && (
        <div className="media-strip" aria-label={`${product.name} media`}>
          {media.map((item, index) => (
            <button className={active === index ? 'is-active' : ''} key={item.id || `${item.url}-${index}`} onClick={() => setActive(index)} type="button" aria-label={`Show media ${index + 1}`}>
              {item.media_type === 'video' ? '▶' : <img src={item.thumbnail_url || item.url} alt="" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onOpen, onAdd }) {
  const variant = product.variants?.[0];
  return (
    <article className="product-card">
      <button className="product-open" onClick={() => onOpen(product)} type="button">
        <ProductVisual product={product} compact />
        <div className="product-copy">
          <span>{product.badge || product.type || 'Collection'}</span>
          <h3>{product.name}</h3>
          <p>{product.style || product.description || 'Product details managed in the catalog.'}</p>
        </div>
      </button>
      <div className="product-buy">
        <div><small>Preview price</small><strong>{money(variant?.price || product.price)}</strong></div>
        <button disabled={!variant || Number(variant.stock) < 1} onClick={() => onAdd(product, variant)} type="button">
          {variant && Number(variant.stock) > 0 ? 'Add' : 'Unavailable'}
        </button>
      </div>
    </article>
  );
}

function ProductDialog({ product, onClose, onAdd }) {
  const [variantId, setVariantId] = useState(product?.variants?.[0]?.id || '');
  useEffect(() => setVariantId(product?.variants?.[0]?.id || ''), [product]);
  if (!product) return null;
  const variant = product.variants?.find((item) => item.id === variantId);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog product-dialog" role="dialog" aria-modal="true" aria-labelledby="product-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="dialog-close" onClick={onClose} type="button" aria-label="Close">×</button>
        <ProductVisual product={product} />
        <div className="dialog-copy">
          <span>{product.badge || product.type}</span>
          <h2 id="product-dialog-title">{product.name}</h2>
          <p>{product.description}</p>
          {product.variants?.length > 1 && (
            <label>Option
              <select value={variantId} onChange={(event) => setVariantId(event.target.value)}>
                {product.variants.map((item) => <option key={item.id} value={item.id}>{item.label} · {money(item.price)}</option>)}
              </select>
            </label>
          )}
          <button className="primary-action" disabled={!variant || Number(variant.stock) < 1} onClick={() => onAdd(product, variant)} type="button">
            Add preview item · {money(variant?.price || product.price)}
          </button>
          <small>Assortment, pricing, claims, and availability require final approval before launch.</small>
        </div>
      </section>
    </div>
  );
}

function CheckoutDialog({ cart, productsById, paymentConfig, onClose }) {
  const [form, setForm] = useState({
    email: '', full_name: '', address: '', apartment: '', city: '', state: '', zip_code: '', country: 'US', phone: '',
    shipping_method: 'standard', coupon_code: '', payment_method: paymentConfig?.stripe ? 'stripe' : 'paypal', notes: '',
  });
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState('');
  const items = cart.map((item) => ({ product_id: item.productId, variant_id: item.variantId, quantity: item.quantity }));

  useEffect(() => {
    getQuote(site.apiBase, items).then(setQuote).catch(() => setStatus('A dedicated Novodom quote service is not connected yet.'));
  }, []);

  async function submit(event) {
    event.preventDefault();
    setStatus('Opening secure checkout…');
    try {
      const result = await createCheckout(site.apiBase, form, items);
      if (!result.redirect_url) throw new Error('Payment provider did not return a checkout URL.');
      window.location.assign(result.redirect_url);
    } catch (error) {
      setStatus(error.message);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog checkout-dialog" role="dialog" aria-modal="true" aria-labelledby="checkout-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="dialog-close" onClick={onClose} type="button" aria-label="Close">×</button>
        <div className="checkout-intro">
          <span>Preview checkout</span>
          <h2 id="checkout-title">U.S. shipping details</h2>
          <p>Checkout remains locked until Novodom has its own backend, merchant account, tax rules, policies, and verified webhook lifecycle.</p>
        </div>
        <form onSubmit={submit}>
          {[['email', 'Email', 'email'], ['full_name', 'Full name', 'text'], ['address', 'Street address', 'text'], ['apartment', 'Apartment (optional)', 'text'], ['city', 'City', 'text'], ['state', 'State code', 'text'], ['zip_code', 'ZIP code', 'text'], ['phone', 'Phone', 'tel']].map(([name, label, type]) => (
            <label key={name}>{label}<input required={name !== 'apartment'} type={type} value={form[name]} onChange={(event) => setForm({ ...form, [name]: event.target.value })} /></label>
          ))}
          <fieldset>
            <legend>Payment</legend>
            {paymentConfig?.stripe && <label><input type="radio" name="payment" checked={form.payment_method === 'stripe'} onChange={() => setForm({ ...form, payment_method: 'stripe' })} /> Card</label>}
            {paymentConfig?.paypal && <label><input type="radio" name="payment" checked={form.payment_method === 'paypal'} onChange={() => setForm({ ...form, payment_method: 'paypal' })} /> PayPal</label>}
            {!paymentConfig?.stripe && !paymentConfig?.paypal && <p>No live payment provider is connected.</p>}
          </fieldset>
          <div className="quote"><span>Total</span><strong>{quote ? money(quote.total) : 'Not available'}</strong></div>
          <button className="primary-action" disabled={!paymentConfig?.checkout_enabled || !quote} type="submit">Continue to payment</button>
          {!paymentConfig?.checkout_enabled && <small>Checkout disabled by launch gate.</small>}
          {status && <p role="status">{status}</p>}
        </form>
        <aside className="checkout-summary">
          {cart.map((item) => <div key={`${item.productId}:${item.variantId}`}><span>{productsById[item.productId]?.name || item.productId} × {item.quantity}</span><strong>{money(item.unitPrice * item.quantity)}</strong></div>)}
        </aside>
      </section>
    </div>
  );
}

function PartnerBrief() {
  const [role, setRole] = useState('Beauty creator');
  const [stage, setStage] = useState('Exploring a first collection');
  const [message, setMessage] = useState('');
  const subject = encodeURIComponent(`Novodom partnership brief — ${role}`);
  const body = encodeURIComponent(`Role: ${role}\nStage: ${stage}\nWhat I want to build: ${message || 'Tell Novodom about your audience, target price, and lash idea.'}`);

  return (
    <form className="partner-form" onSubmit={(event) => { event.preventDefault(); window.location.href = `mailto:${site.partnerEmail}?subject=${subject}&body=${body}`; }}>
      <label>I am a
        <select value={role} onChange={(event) => setRole(event.target.value)}>
          <option>Beauty creator</option><option>Lash tech</option><option>Makeup artist</option><option>Beauty founder</option><option>Wholesale buyer</option>
        </select>
      </label>
      <label>Current stage
        <select value={stage} onChange={(event) => setStage(event.target.value)}>
          <option>Exploring a first collection</option><option>Ready to compare samples</option><option>Already selling and need a supplier</option><option>Need a wholesale assortment</option>
        </select>
      </label>
      <label className="full">Your audience and product idea
        <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Example: soft-glam lashes for beginner Latina beauty customers, $28–$36 target bundle…" />
      </label>
      <button className="primary-action" type="submit">Email my brief</button>
      <small>This preview opens your email app. A production lead form needs a dedicated Novodom API and privacy approval.</small>
    </form>
  );
}

export default function App() {
  const [products, setProducts] = useState(site.fallbackProducts);
  const [blocks, setBlocks] = useState({});
  const [catalogState, setCatalogState] = useState('Preview assortment shown');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeAudience, setActiveAudience] = useState(audienceEdits[0]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState({ checkout_enabled: false, stripe: false, paypal: false });
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(site.theme).forEach(([key, value]) => root.style.setProperty(`--${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`, value));
    Promise.allSettled([getCatalog(site.apiBase), getPublishedContent(site.apiBase), getPaymentConfiguration(site.apiBase)]).then(([catalog, content, payments]) => {
      if (catalog.status === 'fulfilled' && Array.isArray(catalog.value) && catalog.value.length) {
        setProducts(catalog.value);
        setCatalogState('Dedicated catalog connected');
      }
      if (content.status === 'fulfilled') setBlocks(contentMap(content.value));
      if (payments.status === 'fulfilled') setPaymentConfig(payments.value);
    });
  }, []);

  useEffect(() => localStorage.setItem(CART_KEY, JSON.stringify(cart)), [cart]);
  const productsById = useMemo(() => Object.fromEntries(products.map((product) => [product.id, product])), [products]);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cmsHeroImage = published(blocks, 'home.hero.image', 'url', '');
  const heroImage = cmsHeroImage || '/novodom-lash-studio.png';

  function add(product, variant) {
    if (!variant || Number(variant.stock) < 1) return;
    setCart((current) => {
      const key = `${product.id}:${variant.id}`;
      const existing = current.find((item) => `${item.productId}:${item.variantId}` === key);
      if (existing) return current.map((item) => `${item.productId}:${item.variantId}` === key ? { ...item, quantity: Math.min(item.quantity + 1, 10) } : item);
      return [...current, { productId: product.id, variantId: variant.id, quantity: 1, unitPrice: Number(variant.price) }];
    });
    setSelectedProduct(null);
    setCartOpen(true);
  }

  return (
    <main>
      <div className="announcement">{published(blocks, 'site.announcement', 'text', site.announcement)}</div>
      <header className="nav">
        <a className="brand" href="#top"><Wordmark /></a>
        <nav aria-label="Primary">{site.navigation.map((item) => <a key={item.label} href={item.href}>{item.label}</a>)}</nav>
        <button className="cart-button" onClick={() => setCartOpen(!cartOpen)} type="button" aria-expanded={cartOpen}>Cart <span>{cartCount}</span></button>
      </header>

      <section className="hero" id="top">
        <div className="hero-image" role="img" aria-label="False lash sample collection in a warm editorial studio" style={{ backgroundImage: `url(${JSON.stringify(heroImage)})` }} />
        <div className="hero-copy">
          <span>{published(blocks, 'home.hero.eyebrow', 'text', site.hero.eyebrow)}</span>
          <h1>{published(blocks, 'home.hero.title', 'text', site.hero.title)}</h1>
          <p>{published(blocks, 'home.hero.body', 'text', site.hero.body)}</p>
          <div className="hero-actions">
            <a className="primary-action" href={published(blocks, 'home.hero.cta', 'link_url', site.hero.primaryHref)}>{published(blocks, 'home.hero.cta', 'text', site.hero.primaryLabel)}</a>
            <a className="text-action" href="#process">See the process <span>↘</span></a>
          </div>
        </div>
        <aside className="hero-proof"><strong>3</strong><span>signature styles to start</span><i /><strong>21–45</strong><span>day working launch window*</span><small>*After final specifications and written commercial approval.</small></aside>
      </section>

      <section className="trust-row" aria-label="Program principles">{site.trust.map((item) => <span key={item}>{item}</span>)}</section>

      <section className="audience section" id="private-label">
        <div className="section-heading split"><div><span>Choose your starting point</span><h2>Built around the person who already trusts you.</h2></div><p>Novodom is designed as a supply-chain partner first and a retail brand second. Select your role to see the recommended entry path.</p></div>
        <div className="audience-layout">
          <div className="audience-tabs" role="tablist" aria-label="Partner type">
            {audienceEdits.map((item) => <button className={activeAudience.id === item.id ? 'is-active' : ''} key={item.id} onClick={() => setActiveAudience(item)} role="tab" aria-selected={activeAudience.id === item.id} type="button"><span>{item.label}</span><i>↗</i></button>)}
          </div>
          <article className="audience-result">
            <span>Recommended first move</span><h3>{activeAudience.title}</h3><p>{activeAudience.body}</p>
            <ul>{activeAudience.points.map((point) => <li key={point}>{point}</li>)}</ul>
            <a href="#partner-brief">Start this brief</a>
          </article>
        </div>
      </section>

      <section className="studio-band">
        <div><span>Private-label studio</span><h2>One commercial story. Four connected services.</h2></div>
        <div className="service-grid">
          {[['01', 'Product edit', 'Audience, eye shape, mood, comfort, and price architecture.'], ['02', 'Sampling', 'Fiber, band, curl, density, and wear feedback.'], ['03', 'Brand-ready', 'Packaging direction, specifications, and labeling checklist.'], ['04', 'Operations', 'Production milestones, quality checks, and replenishment rhythm.']].map(([number, title, body]) => <article key={number}><strong>{number}</strong><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      </section>

      <section className="process section" id="process">
        <div className="section-heading"><span>From idea to repeatable SKU</span><h2>A launch path that earns the larger order.</h2></div>
        <div className="process-list">{processSteps.map(([number, title, body]) => <article key={number}><strong>{number}</strong><h3>{title}</h3><p>{body}</p></article>)}</div>
      </section>

      <section className="products section" id="products">
        <div className="section-heading split"><div><span>Lash library</span><h2>Sample, test, then scale.</h2></div><p>{catalogState}. These products and prices are creative previews—not live offers.</p></div>
        <div className="product-grid">{products.map((product) => <ProductCard key={product.id} product={product} onOpen={setSelectedProduct} onAdd={add} />)}</div>
      </section>

      <section className="programs section" id="wholesale">
        <div className="section-heading"><span>Commercial paths</span><h2>Choose the commitment that matches the evidence.</h2></div>
        <div className="program-grid">{programCards.map(([title, label, body], index) => <article key={title}><span>0{index + 1}</span><small>{label}</small><h3>{title}</h3><p>{body}</p><a href="#partner-brief">Discuss this path</a></article>)}</div>
      </section>

      <section className="story section" id="story">
        <div className="story-mark"><LashIllustration kind="creator" /><Wordmark /></div>
        <div><span>{published(blocks, 'home.story.eyebrow', 'text', site.story.eyebrow)}</span><h2>{published(blocks, 'home.story.title', 'text', site.story.title)}</h2><p>{published(blocks, 'home.story.body', 'text', site.story.body)}</p></div>
      </section>

      <section className="partner section" id="partner-brief">
        <div className="partner-copy"><span>Start a conversation</span><h2>Tell us what your audience already asks you for.</h2><p>We will use that signal to shape the first sample edit. No production commitment is implied by submitting a brief.</p></div>
        <PartnerBrief />
      </section>

      <section className="launch-note section" id="launch-note">
        <strong>Preview, not a live store.</strong>
        <p>Checkout, taxes, shipping promises, product claims, policies, email delivery, analytics consent, merchant settlement, and search indexing stay disabled until each Novodom launch gate is verified.</p>
      </section>

      <footer>
        <div><Wordmark /><p>Private-label lashes for creators, beauty professionals, and emerging founders.</p></div>
        <div><strong>Explore</strong><a href="#private-label">Private label</a><a href="#products">Lash library</a><a href="#wholesale">Wholesale</a></div>
        <div><strong>Contact</strong><a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a><span>mynovodom.com</span><small>Policies pending owner and adviser approval.</small></div>
        <p className="copyright">© {new Date().getFullYear()} Novodom · Independent preview storefront</p>
      </footer>

      {cartOpen && (
        <aside className="cart-panel" aria-label="Cart">
          <button className="dialog-close" onClick={() => setCartOpen(false)} type="button" aria-label="Close cart">×</button>
          <span>Preview basket</span><h2>Your edit</h2>
          {!cart.length && <p>Your basket is empty.</p>}
          {cart.map((item) => <div className="cart-line" key={`${item.productId}:${item.variantId}`}><span>{productsById[item.productId]?.name || item.productId}<small>Quantity {item.quantity}</small></span><strong>{money(item.unitPrice * item.quantity)}</strong><button onClick={() => setCart(cart.filter((row) => row !== item))} type="button">Remove</button></div>)}
          <button className="primary-action" disabled={!cart.length} onClick={() => { setCartOpen(false); setCheckoutOpen(true); }} type="button">Review checkout</button>
          <small>Payment remains disabled.</small>
        </aside>
      )}
      <ProductDialog product={selectedProduct} onClose={() => setSelectedProduct(null)} onAdd={add} />
      {checkoutOpen && <CheckoutDialog cart={cart} productsById={productsById} paymentConfig={paymentConfig} onClose={() => setCheckoutOpen(false)} />}
    </main>
  );
}
